<!--
📌 Componente: VitrineReparatur.vue
📂 Caminho: src/components/VitrineReparatur.vue
📝 Descrição: Modal fullscreen que apresenta o histórico de reparos de
  um produto. Mostra cabeçalho com QR code, dados técnicos e lista de
  reparos (mais recentes primeiro) com opção de impressão em A4.

👁️ Interface e Interação com o Usuário:
  - Overlay clicável e tecla ESC para fechar
  - Cabeçalho alinhado ao histórico, texto azul e QR preto
  - Timeline vertical com datas e horários
  - Responsivo (coluna única em telas estreitas)
  - Rodapé com ações “Fechar” e “Drucken”

📥 Entrada (Props):
  - `modelValue` (Boolean) - controle de visibilidade (v-model)
  - `produktId` (String) - ID do produto
  - `produktName` (String) - nome do produto
  - `marke` (Object) - { name, modell }
  - `reparaturen` (Array) - lista de reparos

📤 Saída (Emits):
  - `update:modelValue`
  - `closed`

🔗 Dependências:
  - **Bibliotecas externas:** qrcode
-->

<template>
  <transition name="fade">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-[9999] flex items-stretch"
      role="dialog"
      aria-label="Detalhes de reparo do produto"
      aria-modal="true"
      @keydown.esc.prevent.stop="close"
    >
      <div class="absolute inset-0 bg-cinza-900/50" @click="close" />

      <div
        ref="panel"
        class="relative mx-auto my-4 w-full max-w-6xl max-h-[90vh] overflow-y-auto bg-branco text-azul-profundo rounded shadow-xl flex flex-col focus:outline-none focus:ring-2 focus:ring-azul-profundo"
        role="document"
        tabindex="0"
        @click.stop
      >
        <button
          class="absolute top-3 right-3 p-2 rounded-full hover:bg-cinza-claro focus:outline-none focus:ring-2 focus:ring-azul-profundo"
          type="button"
          aria-label="Fechar"
          @click="close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <section class="px-4 sm:px-6 py-4 overflow-y-auto">
          <div
            class="mx-auto w-full max-w-[210mm] bg-branco border border-cinza-200 rounded p-[10mm]"
          >
            <div
              class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
            >
              <div
                class="flex items-center justify-center border rounded p-2 w-24 h-24 self-start"
              >
                <img
                  v-if="qrCodeUrl"
                  :src="qrCodeUrl"
                  alt="QR do produto"
                  class="object-contain max-w-full max-h-full"
                />
                <div v-else class="text-xs text-cinza-500 text-center">
                  Gerando QR...
                </div>
              </div>

              <dl
                class="grid flex-1 gap-y-3 text-sm sm:text-base sm:grid-cols-2 sm:gap-x-8"
              >
                <div>
                  <dt class="tracking-wide text-xs text-cinza-500">
                    {{ $t("vitrine.product.productId") }}
                  </dt>
                  <dd class="font-mono text-lg font-semibold">
                    {{ produktId }}
                  </dd>
                </div>

                <div>
                  <dt class="tracking-wide text-xs text-cinza-500">
                    {{ $t("vitrine.filters.sort.name") }}
                  </dt>
                  <dd class="leading-relaxed">
                    {{ produktName || "-" }}
                  </dd>
                </div>

                <div>
                  <dt class="tracking-wide text-xs text-cinza-500">
                    {{ $t("vitrine.product.brand") }}
                  </dt>
                  <dd class="leading-relaxed">
                    {{ marke?.name || "-" }}
                  </dd>
                </div>

                <div>
                  <dt class="tracking-wide text-xs text-cinza-500">
                    {{ $t("vitrine.product.model") }}
                  </dt>
                  <dd class="leading-relaxed">
                    {{ marke?.modell || "-" }}
                  </dd>
                </div>
              </dl>
            </div>

            <hr class="my-6 border-cinza-200" />

            <p
              v-if="!reparaturenSorted.length"
              class="text-sm text-texto-secundario"
            >
              Nenhum reparo registrado para este produto.
            </p>

            <div v-else class="space-y-4">
              <article
                v-for="(r, idx) in reparaturenSorted"
                :key="idx"
                :class="[
                  'p-4 border rounded text-azul-profundo space-y-3 transition-colors',
                  r.loesung
                    ? 'bg-verde-suave/10 border-verde-suave/40 print:bg-branco'
                    : 'bg-amarelo-dourado/10 border-amarelo-dourado/40 print:bg-branco',
                ]"
              >
                <div>
                  <p class="tracking-wide text-xs text-cinza-500">
                    {{ $t("vitrine.product.status") }}
                  </p>
                  <p class="text-base font-semibold leading-relaxed">
                    {{ formatStatus(r.status) }}
                  </p>
                </div>

                <div>
                  <p class="tracking-wide text-xs text-cinza-500">
                    {{ $t("vitrine.product.createdAt") }}
                  </p>
                  <p
                    class="text-base font-medium leading-relaxed flex items-baseline gap-2"
                  >
                    <span>{{ r._createdDate || "-" }}</span>
                    <span v-if="r._createdTime" class="text-xs text-cinza-500">
                      {{ r._createdTime }}
                    </span>
                  </p>
                </div>

                <div>
                  <p class="tracking-wide text-xs text-cinza-500">
                    {{ $t("vitrine.product.returnedAt") }}
                  </p>
                  <p
                    class="text-base font-medium leading-relaxed flex items-baseline gap-2"
                  >
                    <span>{{ r._returnedDate || "-" }}</span>
                    <span v-if="r._returnedTime" class="text-xs text-cinza-500">
                      {{ r._returnedTime }}
                    </span>
                  </p>
                </div>

                <div class="space-y-2">
                  <div>
                    <p class="tracking-wide text-xs text-cinza-500">
                      {{ $t("vitrine.product.description") }}
                    </p>
                    <p class="whitespace-pre-line leading-relaxed">
                      {{ r.problemBeschreibung || "" }}
                    </p>
                  </div>

                  <div>
                    <p class="tracking-wide text-xs text-cinza-500">
                      {{ $t("repairPrint.report.title") }}
                    </p>
                    <p class="whitespace-pre-line leading-relaxed">
                      {{ r.loesung || "" }}
                    </p>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <footer
          class="px-4 sm:px-6 py-3 border-t border-cinza-200 flex justify-end gap-3"
        >
          <button
            class="px-4 py-2 bg-amarelo-profundo text-branco rounded hover:bg-amarelo-profundo/90 focus:outline-none focus:ring-2 focus:ring-azul-profundo tracking-wide text-sm"
            type="button"
            @click="close"
          >
            {{ $t("vitrine.common.close") }}
          </button>
          <button
            class="px-4 py-2 bg-cinza-900 text-branco rounded hover:bg-texto-principal focus:outline-none focus:ring-2 focus:ring-azul-profundo tracking-wide text-sm"
            type="button"
            @click="printImage"
          >
            {{ $t("repairPrint.actions.print") }}
          </button>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, watch, onMounted, onBeforeUnmount, computed } from "vue";
import { useI18n } from "vue-i18n";
import QRCode from "qrcode";

import { cor } from "@/services/cores";
export default {
  name: "VitrineReparatur",
  props: {
    modelValue: { type: Boolean, required: true },
    produktId: { type: String, required: true },
    produktName: { type: String, default: "" },
    marke: {
      type: Object,
      default: () => ({ name: "", modell: "" }),
    },
    reparaturen: {
      type: Array,
      default: () => [],
    },
    consultedAt: {
      type: [String, Number, Date, Object],
      default: null,
    },
  },
  emits: ["update:modelValue", "closed"],
  setup(props, { emit }) {
    const panel = ref(null);
    const qrCodeUrl = ref(null);
    const printedStamp = ref({ date: "", time: "" });
    const { t } = useI18n();

    const toDate = (value) => {
      if (!value) return null;
      if (value instanceof Date) return value;
      if (typeof value === "number") {
        const result = new Date(value);
        return Number.isNaN(result.getTime()) ? null : result;
      }
      if (value && typeof value.toDate === "function") {
        try {
          const result = value.toDate();
          return result instanceof Date ? result : null;
        } catch (_) {
          return null;
        }
      }
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed) return null;
        const result = new Date(trimmed);
        return Number.isNaN(result.getTime()) ? null : result;
      }
      return null;
    };

    const formatDateLabel = (value) => {
      const date = toDate(value);
      if (!date) return "";
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = String(date.getFullYear());
      return `${day}.${month}.${year}`;
    };

    const formatTimeLabel = (value) => {
      const date = toDate(value);
      if (!date) return "";
      const hour = String(date.getHours()).padStart(2, "0");
      const minute = String(date.getMinutes()).padStart(2, "0");
      return `${hour}:${minute}`;
    };

    const reparaturenSorted = computed(() => {
      const items = Array.isArray(props.reparaturen)
        ? [...props.reparaturen]
        : [];
      return items
        .map((entry) => {
          const created = toDate(entry.erstelltAm);
          const returned = toDate(entry.zurueckgegebenAm);
          return {
            ...entry,
            _dateValue: created?.getTime() || 0,
            _createdDate: formatDateLabel(created),
            _createdTime: formatTimeLabel(created),
            _returnedDate: formatDateLabel(returned),
            _returnedTime: formatTimeLabel(returned),
          };
        })
        .sort((a, b) => b._dateValue - a._dateValue);
    });

    const formatStatus = (value) => {
      if (!value) return "-";
      const base = value.includes("-") ? value.split("-").pop() : value;
      return base
        .replace(
          /([a-z\u00e4\u00f6\u00fc\u00df])([A-Z\u00c4\u00d6\u00dc])/g,
          "$1 $2"
        )
        .trim();
    };

    const RECORDS_PER_PAGE = 2;

    const escapeHtml = (value) => {
      if (value === null || value === undefined) return "";
      return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    };

    const textToHtml = (value) => {
      if (!value) return "";
      return escapeHtml(value).replace(/\r?\n/g, "<br />");
    };

    const chunkRecords = (items) => {
      if (!items || !items.length) {
        return [[]];
      }
      const chunks = [];
      for (let i = 0; i < items.length; i += RECORDS_PER_PAGE) {
        chunks.push(items.slice(i, i + RECORDS_PER_PAGE));
      }
      return chunks;
    };

    const generateQRCode = async () => {
      qrCodeUrl.value = null;
      const id = String(props.produktId || "").trim();
      if (!id) return;
      try {
        qrCodeUrl.value = await QRCode.toDataURL(id);
      } catch (_) {
        qrCodeUrl.value = null;
      }
    };

    const buildHeaderHtml = () => {
      const normalizeValue = (value) => {
        if (value === null || value === undefined) return "-";
        if (typeof value === "string") {
          const trimmed = value.trim();
          return trimmed ? trimmed : "-";
        }
        return value;
      };

      const rows = [
        { label: t("vitrine.product.productId"), value: props.produktId },
        { label: t("vitrine.filters.sort.name"), value: props.produktName },
        { label: t("vitrine.product.brand"), value: props.marke?.name },
        { label: t("vitrine.product.model"), value: props.marke?.modell },
      ];

      const infoHtml = rows
        .map(({ label, value }) => {
          const normalized = normalizeValue(value);
          return `
            <div class="info-item">
              <span class="info-label">${escapeHtml(label)}</span>
              <span class="info-value">${escapeHtml(normalized)}</span>
            </div>
          `;
        })
        .join("");

      const qrContent = qrCodeUrl.value
        ? `<img src="${qrCodeUrl.value}" alt="QR" />`
        : `<div class="qr-placeholder">QR</div>`;

      return `
        <header class="print-header">
          <div class="print-qr">
            ${qrContent}
          </div>
          <div class="print-info">
            ${infoHtml}
          </div>
        </header>
      `;
    };

    const buildRecordSlotHtml = (record) => {
      if (!record) {
        return '<section class="record-slot record-slot--empty"></section>';
      }
      const slotClass = record.loesung
        ? "record-slot record-slot--solved"
        : "record-slot record-slot--pending";
      const statusLabel = escapeHtml(t("vitrine.product.status"));
      const statusValue = escapeHtml(formatStatus(record.status) || "-");
      const createdLabel = escapeHtml(t("vitrine.product.createdAt"));
      const returnedLabel = escapeHtml(t("vitrine.product.returnedAt"));
      const descriptionLabel = escapeHtml(t("vitrine.product.description"));
      const solutionLabel = escapeHtml(t("repairPrint.report.title"));
      const createdDate = escapeHtml(record._createdDate || "-");
      const createdTime = record._createdTime
        ? `<span class="record-time">${escapeHtml(record._createdTime)}</span>`
        : "";
      const returnedDate = escapeHtml(record._returnedDate || "-");
      const returnedTime = record._returnedTime
        ? `<span class="record-time">${escapeHtml(record._returnedTime)}</span>`
        : "";
      const hasProblem =
        typeof record.problemBeschreibung === "string" &&
        record.problemBeschreibung.trim();
      const hasSolution =
        typeof record.loesung === "string" && record.loesung.trim();
      const problemText = hasProblem ? record.problemBeschreibung : "-";
      const solutionText = hasSolution ? record.loesung : "-";

      return `
        <section class="${slotClass}">
          <div class="record-line">
            <span class="record-label">${statusLabel}</span>
            <span class="record-value">${statusValue}</span>
          </div>
          <div class="record-line">
            <span class="record-label">${createdLabel}</span>
            <span class="record-value record-value--with-time">
              <span class="record-date">${createdDate}</span>
              ${createdTime}
            </span>
          </div>
          <div class="record-line">
            <span class="record-label">${returnedLabel}</span>
            <span class="record-value record-value--with-time">
              <span class="record-date">${returnedDate}</span>
              ${returnedTime}
            </span>
          </div>
          <div class="record-text">
            <span class="record-label">${descriptionLabel}</span>
            <p class="record-paragraph">${textToHtml(problemText)}</p>
          </div>
          <div class="record-text">
            <span class="record-label">${solutionLabel}</span>
            <p class="record-paragraph">${textToHtml(solutionText)}</p>
          </div>
        </section>
      `;
    };

    const buildPageHtml = (records) => {
      const slots = [...records];
      while (slots.length < RECORDS_PER_PAGE) {
        slots.push(null);
      }
      const slotsHtml = slots.map((slot) => buildRecordSlotHtml(slot)).join("");
      const footerLabel =
        printedStamp.value.date && printedStamp.value.time
          ? `Praxis GmbH – ${printedStamp.value.date} ${printedStamp.value.time}`
          : "Praxis GmbH";

      return `
        <section class="print-page">
          ${buildHeaderHtml()}
          <div class="print-records">
            ${slotsHtml}
          </div>
          <footer class="print-footer">${escapeHtml(footerLabel)}</footer>
        </section>
      `;
    };

    const buildPrintHtml = (title) => {
      const safeTitle = escapeHtml(title);
      const pages = chunkRecords(reparaturenSorted.value).map((chunk) =>
        buildPageHtml(chunk)
      );
      const pagesHtml = pages.join("");

      const styles = `
        @page { size: A4; margin: 0; }
        body {
          margin: 0;
          background: ${cor("branco")};
          font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
          color: ${cor("azul-escuro")};
        }
        .print-page {
          width: 210mm;
          min-height: 297mm;
          box-sizing: border-box;
          padding: 18mm 16mm 14mm;
          display: flex;
          flex-direction: column;
          gap: 10mm;
          page-break-after: always;
        }
        .print-page:last-of-type {
          page-break-after: auto;
        }
        .print-header {
          display: flex;
          gap: 10mm;
          align-items: flex-start;
        }
        .print-qr {
          width: 38mm;
          height: 38mm;
          border: 0.4mm solid ${cor("borda-suave")};
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${cor("branco")};
          padding: 2mm;
          box-sizing: border-box;
        }
        .print-qr img {
          max-width: 100%;
          max-height: 100%;
        }
        .qr-placeholder {
          font-size: 3.2mm;
          color: ${cor("cinza-400")};
          text-transform: uppercase;
          letter-spacing: 0.3mm;
        }
        .print-info {
          flex: 1;
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 4mm 8mm;
        }
        .info-item {
          display: flex;
          flex-direction: column;
          gap: 1.2mm;
        }
        .info-label {
          font-size: 3mm;
          color: ${cor("cinza-500")};
          text-transform: uppercase;
          letter-spacing: 0.3mm;
        }
        .info-value {
          font-size: 4mm;
          font-weight: 600;
          color: ${cor("azul-escuro")};
          word-break: break-word;
        }
        .print-records {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 10mm;
        }
        .record-slot {
          flex: 1;
          border: 0.4mm solid ${cor("borda-suave")};
          border-radius: 4px;
          padding: 6mm;
          box-sizing: border-box;
          background: ${cor("branco")};
          display: flex;
          flex-direction: column;
          gap: 4mm;
        }
        .record-slot--solved {
          border-color: ${cor("verde-suave")};
          background: ${cor("verde-fundo")};
        }
        .record-slot--pending {
          border-color: ${cor("amarelo-dourado")};
          background: ${cor("amarelo-fundo")};
        }
        .record-slot--empty {
          visibility: hidden;
        }
        .record-line {
          display: flex;
          flex-direction: column;
          gap: 1.8mm;
        }
        .record-label {
          font-size: 3mm;
          color: ${cor("cinza-500")};
          text-transform: uppercase;
          letter-spacing: 0.3mm;
        }
        .record-value {
          font-size: 4mm;
          font-weight: 600;
          color: ${cor("azul-escuro")};
        }
        .record-value--with-time {
          display: flex;
          align-items: baseline;
          gap: 3mm;
        }
        .record-time {
          font-size: 3mm;
          color: ${cor("cinza-500")};
          font-weight: 400;
        }
        .record-text {
          display: flex;
          flex-direction: column;
          gap: 1.8mm;
        }
        .record-paragraph {
          font-size: 3.4mm;
          line-height: 1.4;
          color: ${cor("texto-principal")};
          word-break: break-word;
        }
        .print-footer {
          text-align: center;
          font-size: 3mm;
          color: ${cor("cinza-500")};
        }
      `;

      return `<!DOCTYPE html>
<html lang="de">
  <head>
    <meta charset="utf-8" />
    <title>${safeTitle}</title>
    <style>${styles}</style>
  </head>
  <body>
    ${pagesHtml}
    <script>
      window.addEventListener('load', function () {
        window.focus();
        window.print();
        setTimeout(function () {
          window.close();
        }, 200);
      });
    </scr${""}ipt>
  </body>
</html>`;
    };

    const focusPanel = () => {
      if (panel.value && panel.value.focus) panel.value.focus();
    };

    const close = () => {
      emit("update:modelValue", false);
      emit("closed");
      try {
        document.body.style.overflow = "";
      } catch (_) {
        /* ignore */
      }
    };

    const onKey = (event) => {
      if (event?.key === "Escape") close();
    };

    const setPrintedStamp = () => {
      const base = toDate(props.consultedAt);
      if (!base) {
        printedStamp.value = { date: "", time: "" };
        return;
      }
      printedStamp.value = {
        date: formatDateLabel(base),
        time: formatTimeLabel(base),
      };
    };

    const printImage = async () => {
      try {
        setPrintedStamp();
        if (!printedStamp.value.date || !printedStamp.value.time) {
          const now = new Date();
          printedStamp.value = {
            date: formatDateLabel(now),
            time: formatTimeLabel(now),
          };
        }
        if (!qrCodeUrl.value) {
          await generateQRCode();
        }
        const popup = window.open("", "_blank");
        if (!popup) return;
        const fileName = `Reparatur_${props.produktId || "produkt"}`.replace(
          /[^\w.-]+/gu,
          "_"
        );
        const html = buildPrintHtml(fileName);
        popup.document.open();
        popup.document.write(html);
        popup.document.close();
      } catch (error) {
        console.error(
          "[VitrineReparatur] Failed to generate print view:",
          error
        );
      }
    };

    watch(
      () => props.modelValue,
      (open) => {
        try {
          document.body.style.overflow = open ? "hidden" : "";
        } catch (_) {
          /* ignore */
        }
        if (open) {
          focusPanel();
          generateQRCode();
          setPrintedStamp();
        } else {
          printedStamp.value = { date: "", time: "" };
        }
      },
      { immediate: true }
    );

    watch(
      () => props.produktId,
      () => generateQRCode()
    );

    watch(
      () => props.consultedAt,
      () => setPrintedStamp()
    );

    onMounted(() => {
      window.addEventListener("keydown", onKey);
      if (props.modelValue) focusPanel();
    });

    onBeforeUnmount(() => {
      window.removeEventListener("keydown", onKey);
      try {
        document.body.style.overflow = "";
      } catch (_) {
        /* ignore */
      }
    });

    return {
      panel,
      qrCodeUrl,
      reparaturenSorted,
      formatStatus,
      printImage,
      close,
    };
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
